// public/v3/src/components/bhi-no-assessment-review-modal.jsx — SOFT-guard
// override confirmation for BHI (99484) when NO initial rating-scale
// assessment is on file for the service period.
//
// The BHI billing gate normally treats a missing initial assessment as a hard
// blocker. CMS expects an initial assessment with a validated rating scale
// (e.g., PHQ-9/PHQ-2) for General BHI. When none is documented, the operator
// may still have grounds to proceed (e.g., the scale lives outside the
// structured chart, or the practitioner is attesting from an external source)
// — but that must be an EXPLICIT, WARNED decision, not a silent bypass.
//
// This modal mirrors ComplexCcmReviewModal: pure UI, decision persisted +
// audited by the caller (studio.jsx). onProceed fires when the operator
// explicitly confirms; the caller then threads { assessmentOverride: true }
// into the generate/push path. The generated note's attestation is softened
// downstream so it does NOT falsely assert an assessment was documented.

import React, { useEffect } from 'react';

function formatMonthLongFromKey(monthKey) {
  if (!monthKey || typeof monthKey !== 'string') return '';
  const m = monthKey.match(/^(\d{4})-(\d{2})/);
  if (!m) return '';
  const y = Number(m[1]);
  const mo = Number(m[2]);
  if (!Number.isFinite(y) || !Number.isFinite(mo)) return '';
  const d = new Date(y, mo - 1, 1);
  return d.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
}

// mode: 'generate' | 'push' — only changes the action verb in the copy/button.
export function BhiNoAssessmentReviewModal({ patient, monthStr, mode, onClose, onProceed, busy }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape' && !busy) onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, busy]);

  const raw = patient?._raw || patient || {};
  const name = ((raw.fname || '') + ' ' + (raw.lname || '')).trim() || patient?.name || 'this patient';
  const currentMonth = formatMonthLongFromKey(monthStr);
  const verb = mode === 'push' ? 'Push & Sign' : 'Generate';

  return (
    <div className="modal-backdrop" onClick={() => { if (!busy) onClose(); }}>
      <div
        className="modal"
        role="dialog"
        aria-modal="true"
        aria-label={`No initial BHI assessment for ${name}`}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 560 }}>
        <div className="modal-head">
          <h3>No initial BHI assessment on file — {name}{currentMonth ? ` · ${currentMonth}` : ''}</h3>
        </div>
        <div className="modal-body">
          <p style={{ marginTop: 0 }}>
            <b>No initial (PHQ-9/PHQ-2) assessment is on file</b> for {name} for this period.
            General Behavioral Health Integration (99484) expects an initial assessment with a
            validated rating scale.
          </p>
          <p>
            You can proceed anyway. If you do, the billing note will <b>not</b> claim a rating-scale
            assessment was documented — it will state plainly that none is on file for the period,
            and this override will be recorded in the audit log.
          </p>
          <p style={{ marginBottom: 0, color: 'var(--ink-3)', fontSize: 13 }}>
            Only proceed if the assessment is documented elsewhere or the billing practitioner is
            attesting to it from another source. Otherwise, cancel and record the assessment first.
          </p>
        </div>
        <div className="modal-foot">
          <button className="btn" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn warn" onClick={() => { if (!busy) onProceed(); }} disabled={busy}>
            {busy ? 'Working…' : `Proceed without assessment — ${verb}`}
          </button>
        </div>
      </div>
    </div>
  );
}
